import { FontFamily } from "@redotech/redo-content-builder-common/element-schema";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { pageLayoutSchema } from "../packing-slips/packing-slip-definition";

/** Maximum number of products to fetch for AI recommendations */
export const MAX_PRODUCTS_FOR_RECOMMENDATIONS = 50;

export enum PersonalizedSlipBlockType {
  DISCOUNT = "Discount",
  IMAGE = "Image",
  LINE = "Line",
  PRODUCT_RECOMMENDATION = "AI product recommendation",
  QR_CODE = "QR Code",
  SHAPE = "Shape",
  SPACER = "Spacer",
  TEXT = "Text",
}

// Shape type identifiers — mostly sourced from `redo/icons/src/<name>_filled.svg`.
// Keep in sync with SHAPE_DEFS in fulfillment/documents/.../blocks/shape-paths.ts.
export const slipShapeType = [
  // Basic shapes (incl. polygons)
  "square",
  "rounded-square",
  "circle",
  "triangle",
  "pentagon",
  "hexagon-1",
  "hexagon-2",
  "octagon",
  // Stars
  "star-01",
  "star-02",
  "star-03",
  "star-04",
  "star-06",
  "star-07",
  "asterisk-1",
  "stars-1",
  "stars-2",
  "stars-3",
  // Arrows — chunky block + skinny narrow
  "arrow-up",
  "arrow-down",
  "arrow-left",
  "arrow-right",
  "arrow-narrow-up",
  "arrow-narrow-down",
  "arrow-narrow-left",
  "arrow-narrow-right",
  // Hearts
  "heart",
  "hearts",
  "heart-rounded",
  "heart-square",
  "heart-circle",
  "heart-hand",
  // Banners
  "flag-01",
  "flag-03",
  "flag-04",
  "bookmark",
  // Celestial
  "cloud",
  "sun",
  "sunrise",
  "sunset",
  "moon-1",
  "moon-2",
  "lightning-1",
  "lightning-2",
  "magic-wand-1",
  "magic-wand-2",
  // Awards
  "award-01",
  "award-02",
  "award-03",
  "award-04",
  "award-05",
  "certificate-1",
  "certificate-2",
] as const;
export type SlipShapeType = (typeof slipShapeType)[number];

export const slipLineStyle = ["solid", "dashed", "dotted"] as const;
export type SlipLineStyle = (typeof slipLineStyle)[number];

export const slipBlockSide = ["front", "back"] as const;
export type SlipBlockSide = (typeof slipBlockSide)[number];

const personalizedSlipSettingsSchema_ = z.object({
  pageLayout: pageLayoutSchema,
  backgroundColor: z.string().nullish(),
  backgroundImageUrl: z.string().nullish(),
  backgroundImageOpacity: z.number().nullish(),
  twoSided: z.boolean().nullish(),
  backBackgroundColor: z.string().nullish(),
  backBackgroundImageUrl: z.string().nullish(),
  backBackgroundImageOpacity: z.number().nullish(),
  fontFamily: z.string().nullish(),
  fontColor: z.string().nullish(),
  fontSize: z.number().nullish(),
});

export const personalizedSlipSettingsSchema: z.ZodType<PersonalizedSlipSettings> =
  personalizedSlipSettingsSchema_;
export interface PersonalizedSlipSettings extends z.infer<
  typeof personalizedSlipSettingsSchema_
> {}

const baseSectionSchema_ = z.object({
  id: z.string().nullish(),
  type: createEnum(PersonalizedSlipBlockType, {
    enumName: "PersonalizedSlipBlockType",
  }),
  x: z.number(),
  y: z.number(),
  zIndex: z.number(),
  width: z.number(),
  height: z.number(),
  rotation: z.number(),
  side: createEnum(slipBlockSide, { enumName: "SlipBlockSide" }).default(
    "front",
  ),
  backgroundColor: z.string().nullish(),
  borderColor: z.string().nullish(),
  borderWidth: z.number().nullish(),
  borderRadius: z.number().nullish(),
});

export const baseSectionSchema: z.ZodType<BaseBlock> = baseSectionSchema_;
export interface BaseBlock extends z.infer<typeof baseSectionSchema_> {}

export const textAlignment = ["left", "center", "right"] as const;
export type TextAlignment = (typeof textAlignment)[number];

export const textBlockType = ["static", "title", "subtitle", "custom"] as const;
export type TextBlockType = (typeof textBlockType)[number];

/** Text block types that are AI-generated (everything except "static") */
export const aiGeneratedTextTypes: readonly TextBlockType[] = [
  "title",
  "subtitle",
  "custom",
];

/** Check if a text type requires AI generation */
export function isAiGeneratedTextType(
  textType: TextBlockType | null | undefined,
): boolean {
  return textType != null && aiGeneratedTextTypes.includes(textType);
}

export const fontWeight = [
  "normal",
  "bold",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
] as const;
export type FontWeight = (typeof fontWeight)[number];

const textSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.TEXT),
  text: z.string().nullish(),
  linkColor: z.string().nullish(),
  fontSize: z.number().nullish(),
  fontFamily: createEnum(FontFamily, { enumName: "FontFamily" }).nullish(),
  fontColor: z.string().nullish(),
  // Typography enhancements
  fontWeight: createEnum(fontWeight, { enumName: "SlipFontWeight" }).nullish(),
  textAlignment: createEnum(textAlignment, {
    enumName: "SlipTextAlignment",
  }).nullish(),
  lineHeight: z.number().nullish(),
  letterSpacing: z.number().nullish(),
  // Background image
  backgroundImage: z.string().nullish(),
  backgroundOverlayColor: z.string().nullish(),
  backgroundOverlayOpacity: z.number().min(0).max(100).nullish(),
  // AI text generation
  textType: createEnum(textBlockType, {
    enumName: "SlipTextBlockType",
  }).nullish(),
  aiPrompt: z.string().nullish(), // Only used when textType === 'custom'
});

export const textSectionSchema: z.ZodType<TextBlock> = textSectionSchema_;
export interface TextBlock extends z.infer<typeof textSectionSchema_> {}

const qrCodeSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.QR_CODE),
  qrCode: z.string(),
});
export const qrCodeSectionSchema: z.ZodType<QrCodeBlock> = qrCodeSectionSchema_;
export interface QrCodeBlock extends z.infer<typeof qrCodeSectionSchema_> {}

export const imageObjectFit = ["fill", "fit", "cover"] as const;
export type ImageObjectFit = (typeof imageObjectFit)[number];

const imageSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.IMAGE),
  imageUrl: z.string().nullish(),
  resolution: z.number().nullish(),
  objectFit: createEnum(imageObjectFit, {
    enumName: "SlipImageObjectFit",
  }).nullish(),
  overlayColor: z.string().nullish(),
  overlayOpacity: z.number().min(0).max(100).nullish(),
});

export const imageSectionSchema: z.ZodType<ImageBlock> = imageSectionSchema_;
export interface ImageBlock extends z.infer<typeof imageSectionSchema_> {}

export const productRecommendationLayout = [
  "adjacent",
  "overlay",
  "image-only",
] as const;
export type ProductRecommendationLayout =
  (typeof productRecommendationLayout)[number];

// This createEnum literal is the source of truth that generates the
// slip_product_selection_mode CongoDB enum — it can't import the codegen it
// produces (circular), and Zod's z.enum(nativeEnum) would force every consumer
// onto the nominal enum type. Matches the other slip enums in this file.
//redontIgnore
export const productSelectionMode = [
  "ai",
  "static",
  "recently-ordered",
  "warm",
] as const;
export type ProductSelectionMode = (typeof productSelectionMode)[number];

// Text placement relative to the product image (no corners)
export const textPlacement = [
  "top", // Text above image
  "left", // Text to the left of image
  "right", // Text to the right of image
  "bottom", // Text below image
] as const;
export type TextPlacement = (typeof textPlacement)[number];

export const productAspectRatio = ["3:2", "1:1", "4:5", "2:3"] as const;
export type ProductAspectRatio = (typeof productAspectRatio)[number];

// Vertical alignment within the text area
export const verticalAlignment = ["top", "center", "bottom"] as const;
export type VerticalAlignment = (typeof verticalAlignment)[number];

const productRecommendationSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.PRODUCT_RECOMMENDATION),

  // Product selection
  productSelectionMode: createEnum(productSelectionMode, {
    enumName: "SlipProductSelectionMode",
  }).nullish(), // Default: "ai"
  staticProductId: z.string().nullish(), // Used when mode is "static"
  staticProductTitle: z.string().nullish(), // Cached for display
  staticVariantTitle: z.string().nullish(), // Cached variant title for display (null/"Default Title" variants are omitted)
  staticProductImageUrl: z.string().nullish(), // Cached for display
  staticProductPrice: z.string().nullish(), // Cached for display
  numberOfProducts: z.number().nullish(), // Number of products to show (default: 1)

  // Title display preference
  useVariantTitle: z.boolean().nullish(), // When true, display the variant title instead of the product title

  // Layout & positioning
  layout: createEnum(productRecommendationLayout, {
    enumName: "SlipProductRecommendationLayout",
  }).nullish(),
  textPlacement: createEnum(textPlacement, {
    enumName: "SlipTextPlacement",
  }).nullish(), // Default: "right"
  verticalAlignment: createEnum(verticalAlignment, {
    enumName: "SlipVerticalAlignment",
  }).nullish(), // Default: "center"
  aspectRatio: createEnum(productAspectRatio, {
    enumName: "SlipProductAspectRatio",
  }).nullish(), // Default: "1:1"

  // Display toggles
  showProductTitle: z.boolean().nullish(),
  hideProductTitle: z.boolean().nullish(),
  showProductPrice: z.boolean().nullish(), // Deprecated - use hideProductPrice instead
  hideProductPrice: z.boolean().nullish(),
  showDiscountCode: z.boolean().nullish(),

  // Image styling
  imageRadius: z.number().nullish(),
  imageBorderColor: z.string().nullish(),
  imageBorderWidth: z.number().nullish(),
  imageObjectFit: createEnum(imageObjectFit, {
    enumName: "SlipImageObjectFit",
  }).nullish(), // Image constraints: fill, fit, cover
  marginHorizontal: z.number().nullish(),
  marginVertical: z.number().nullish(),

  // Text styling
  fontFamily: createEnum(FontFamily, { enumName: "FontFamily" }).nullish(),
  fontSize: z.number().nullish(),
  textColor: z.string().nullish(),
  titleFontSize: z.number().nullish(),
  titleFontColor: z.string().nullish(),
  titleFontWeight: createEnum(fontWeight, {
    enumName: "SlipFontWeight",
  }).nullish(),
  textAlignment: createEnum(textAlignment, {
    enumName: "SlipTextAlignment",
  }).nullish(),
  lineHeight: z.number().nullish(),
  letterSpacing: z.number().nullish(),
  priceFontSize: z.number().nullish(),
  priceFontColor: z.string().nullish(),
  discountFontSize: z.number().nullish(),
  discountFontColor: z.string().nullish(),

  // Generated content (filled at generation time)
  generatedProductId: z.string().nullish(),
  generatedProductTitle: z.string().nullish(),
  generatedProductDescription: z.string().nullish(),
  generatedProductPrice: z.string().nullish(),
  generatedProductImageUrl: z.string().nullish(),
  generatedDiscountCode: z.string().nullish(),
});

export const productRecommendationSectionSchema: z.ZodType<ProductRecommendationBlock> =
  productRecommendationSectionSchema_;
export interface ProductRecommendationBlock extends z.infer<
  typeof productRecommendationSectionSchema_
> {}

// LINE block - simple divider line, optionally styled and with arrow caps.
const lineSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.LINE),
  lineColor: z.string().nullish(),
  lineThickness: z.number().nullish(),
  lineStyle: createEnum(slipLineStyle, { enumName: "SlipLineStyle" }).nullish(),
  arrowStart: z.boolean().nullish(),
  arrowEnd: z.boolean().nullish(),
});

export const lineSectionSchema: z.ZodType<LineBlock> = lineSectionSchema_;
export interface LineBlock extends z.infer<typeof lineSectionSchema_> {}

// SHAPE block - geometric or decorative shape rendered as an SVG path.
const shapeSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.SHAPE),
  shapeType: createEnum(slipShapeType, { enumName: "SlipShapeType" }),
  fillColor: z.string().nullish(),
  opacity: z.number().min(0).max(100).nullish(),
});

export const shapeSectionSchema: z.ZodType<ShapeBlock> = shapeSectionSchema_;
export interface ShapeBlock extends z.infer<typeof shapeSectionSchema_> {}

// SPACER block - empty space for layout
const spacerSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.SPACER),
});
export const spacerSectionSchema: z.ZodType<SpacerBlock> = spacerSectionSchema_;
export interface SpacerBlock extends z.infer<typeof spacerSectionSchema_> {}

// Offer text type for discount block
export const offerTextType = ["ai", "static", "none"] as const;
export type OfferTextType = (typeof offerTextType)[number];

export const qrCodePosition = ["left", "right"] as const;
export type QrCodePosition = (typeof qrCodePosition)[number];

// DISCOUNT block - displays discount with optional QR code
const discountSectionSchema_ = baseSectionSchema_.extend({
  type: z.literal(PersonalizedSlipBlockType.DISCOUNT),
  // Discount selection
  discountId: zExt.objectId().nullish(),
  generateUniquePerCustomer: z.boolean().nullish(),
  // Offer text
  offerTextType: createEnum(offerTextType, {
    enumName: "SlipOfferTextType",
  }).nullish(),
  offerText: z.string().nullish(),
  // QR code
  includeQrCode: z.boolean().nullish(),
  qrCodeUrl: z.string().nullish(),
  qrCodePosition: createEnum(qrCodePosition, {
    enumName: "SlipQrCodePosition",
  }).nullish(),
  // Styling
  fontFamily: createEnum(FontFamily, { enumName: "FontFamily" }).nullish(),
  fontSize: z.number().nullish(),
  fontWeight: createEnum(fontWeight, { enumName: "SlipFontWeight" }).nullish(),
  textAlignment: createEnum(textAlignment, {
    enumName: "SlipTextAlignment",
  }).nullish(),
  textColor: z.string().nullish(),
  blockBackgroundColor: z.string().nullish(),
  // Generated content (filled at generation time)
  generatedDiscountCode: z.string().nullish(),
  generatedOfferText: z.string().nullish(),
});

export const discountSectionSchema: z.ZodType<DiscountBlock> =
  discountSectionSchema_;
export interface DiscountBlock extends z.infer<typeof discountSectionSchema_> {}

const personalizedSlipBlockSchema_ = z.discriminatedUnion("type", [
  textSectionSchema_,
  qrCodeSectionSchema_,
  imageSectionSchema_,
  productRecommendationSectionSchema_,
  lineSectionSchema_,
  spacerSectionSchema_,
  discountSectionSchema_,
  shapeSectionSchema_,
]);
export const personalizedSlipBlockSchema: z.ZodType<PersonalizedSlipBlock> =
  personalizedSlipBlockSchema_;

export type PersonalizedSlipBlock = z.infer<
  typeof personalizedSlipBlockSchema_
>;

const personalizedSlipSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  createdAt: z.string(),
  updatedAt: z.string(),
  name: z.string(),
  blocks: z.array(personalizedSlipBlockSchema),
  globalSettings: personalizedSlipSettingsSchema,
});
export const personalizedSlipSchema: z.ZodType<PersonalizedSlip> =
  personalizedSlipSchema_;
export interface PersonalizedSlip extends z.infer<
  typeof personalizedSlipSchema_
> {}

const personalizedSlipVariablesSchema = z.object({
  customerFirstName: z.string(),
  customerLastName: z.string(),
  customerFullName: z.string(),
  orderedProductName: z.string().nullish(),
  orderedProductFamilyName: z.string().nullish(),
  orderedProductCategory: z.string().nullish(),
  recommendedProductName: z.string().nullish(),
  discountUrl: z.string().nullish(),
  discountCode: z.string().nullish(),
});

export type PersonalizedSlipVariables = z.infer<
  typeof personalizedSlipVariablesSchema
>;

export const RICH_TEXT_VARIABLES: (keyof PersonalizedSlipVariables)[] = [
  "customerFirstName",
  "customerLastName",
  "customerFullName",
  "orderedProductName",
  "orderedProductFamilyName",
  "orderedProductCategory",
  "recommendedProductName",
  "discountUrl",
  "discountCode",
];

/**
 * Function type for generating product recommendations.
 * This is used for dependency injection to avoid circular dependencies
 * between redo/server and recommendations-engine.
 */
export type GenerateProductRecommendationsFunc = (params: {
  teamId: string;
  productIds: string[];
  customerEmail?: string;
  limit?: number;
}) => Promise<{ productIds: string[] }>;
